import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Badge, Card, CardBody, CardFooter, Col, Label } from 'reactstrap';
import {
  getAvailableItemAdditionGroups,
  getItemDataOrAdditionPrice,
  getItemDataPriceWithPreSelections,
  getEffectiveRequestedAt,
  getLocalizedDescription,
  getLocalizedName,
  isOrderEnabled,
  trimText,
  isOrderConditionClassAvailable,
  hasSatisfiedOrderCondition,
  getServiceTimesByPriorty,
  getNextStart,
  getOrderTypesWithoutPosIntegration,
  getOrderConfig,
} from '../../../../utility/util/util.service';
import LazyImage from '../../lazyload/lazy-image/lazy-image.component';
import * as Icon from 'react-feather';
import classnames from 'classnames';
import { connect } from 'react-redux';
import RemoveConfirmationModal from '../../modals/remove-confirmation/remove-confirmation.modal';
import { handleLike, removeItem } from './item-card.service';
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import ItemCardCarousel from './item-card-carousel.component';
import SkeletonLoader from '../../lazyload/skeleton-loader/skeleton-loader.component';
import {
  setCreateItemModalData,
  setEditMenuEntryCategoryModalData,
  setEditItemModalData,
  setIsCreateItemModalOpen,
  setIsEditMenuEntryCategoryModalOpen,
  setIsEditItemModalOpen,
  setIsItemInfoModalOpen,
  setIsSelectAdditionsModalOpen,
  setItemInfoModalData,
  setSelectAdditionsModalData,
} from '../../../../redux/modal/modal.actions';
import {
  addCartItem,
  fetchAdditionGroups,
  fetchFoodItemAdditions,
  fetchItemDatas,
  fetchMenuEntries,
  fetchMenuItems,
  removeCartItem,
} from '../../../../redux/store/store.actions';
import { fetchMe, fetchOnAuthenticated } from '../../../../redux/me/me.actions';
import { SocketContext } from '../../../../context/socket.context';
import { createGuest } from '../../../../utility/sb-api/sb-api.service';
import DisplayTypeField from '../../form/fields/DisplayTypeField';
import TagManager from 'react-gtm-module/dist/TagManager';
import moment from 'moment';
import Marquee from '../../helpers/marquee.component';
import {
  ORDER_TYPE_INHOUSE,
  ORDER_TYPE_PICKUP,
} from '../../../../utility/constants';

const ItemCard = ({
  menuEntry,
  isAdminMode,

  me,
  store,
  cart,
  table,
  kiosk,
  menuEntries,
  posIntegration,
  checkout,

  setItemInfoModalData,
  setIsItemInfoModalOpen,
  fetchMenuEntries,
  fetchMe,
  setSelectAdditionsModalData,
  setIsSelectAdditionsModalOpen,
  addCartItem,
  removeCartItem,
  setEditItemModalData,
  setIsEditItemModalOpen,
  setCreateItemModalData,
  setIsCreateItemModalOpen,
  fetchOnAuthenticated,
  setIsEditMenuEntryCategoryModalOpen,
  setEditMenuEntryCategoryModalData,
  fetchAdditionGroups,
  fetchFoodItemAdditions,
  fetchMenuItems,
  fetchItemDatas,
  dailyLimits,
}) => {
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [showLikeAnimation, setShowLikeAnimation] = useState(false);
  const [showAddToCartAnimation, setShowAddToCartAnimation] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [hasAddedItemData, setHasAddedItemData] = useState(false);
  const [renderServiceTimeBadge, setRenderServiceTimeBadge] = useState(false);
  const socketContext = useContext(SocketContext);

  const intl = useIntl();
  const displayType = menuEntry?.displayType;
  const item = menuEntry?.itemEntry;
  const itemData = useMemo(
    () =>
      item?.data &&
      [...item.data].sort((a, b) => a.manualRanking - b.manualRanking),
    [item?.data],
  );
  const activeItemData = itemData?.[activeIndex];
  // Use orderType for price calculation, fallback to PICKUP for takeaway prices when no orderType is set
  const priceOrderType = isAdminMode
    ? ORDER_TYPE_INHOUSE
    : checkout?.orderType || ORDER_TYPE_PICKUP;
  const activeItemDataPrice =
    store?.selfServiceOrderConfig?.showCombinationPrice ||
    store?.pickupOrderConfig?.showCombinationPrice
      ? getItemDataPriceWithPreSelections(
          priceOrderType,
          activeItemData,
          null,
          getEffectiveRequestedAt(checkout),
        )
      : getItemDataOrAdditionPrice(priceOrderType, activeItemData);
  const _isOrderEnabled =
    !activeItemData?.hiddenCategoryOnly &&
    isOrderEnabled(store, activeItemData, cart, checkout, table, kiosk);
  const posAvailableOrderTypes = getOrderTypesWithoutPosIntegration(store, activeItemData);
  const displayImageOnly = menuEntry?.displayType === 'image_only';
  const displayTextOnly = menuEntry?.displayType === 'text_only';
  const displayFull = menuEntry?.displayType === 'full';
  const displayHidden = menuEntry?.displayType === 'hidden';
  const displayImage = displayFull || displayImageOnly;
  const displayText = displayFull || displayTextOnly;
  const customerProfile = me?.requester?.customerProfile;
  const itemName = getLocalizedName(activeItemData, intl.locale);
  const itemDescription = getLocalizedDescription(activeItemData, intl.locale);
  const isLiked = item && customerProfile?.likedItemIds?.includes(item.id);
  const itemHasVariations = itemData?.length > 1;
  const itemHasImages = !!itemData?.filter(({ image }) => !!image).length;
  const preferenceImages = activeItemData?.preferences
    ?.sort((a, b) => a.id.localeCompare(b.id))
    .map((preference, index) => (
      <img
        key={'preference-' + index}
        height={22}
        src={`/assets/icons/preferences/${preference.iconName}`}
      />
    ));
  const itemImageVisible = !!(itemHasImages && displayImage);
  const itemNameVisible = !!(itemName && displayText);
  const [hashToRemove, setHashToRemove] = useState(null);

  moment.locale(intl.locale);

  useEffect(() => {
    removeCartItem(cart, hashToRemove);
  }, [hashToRemove]);

  // Daily limit info for the active item data
  const dailyLimitInfo = useMemo(
    () => dailyLimits?.find((dl) => dl.itemDataId === activeItemData?.id),
    [dailyLimits, activeItemData?.id],
  );
  const isSoldOut =
    dailyLimitInfo != null && dailyLimitInfo.remaining <= 0;
  const remaining =
    dailyLimitInfo != null ? dailyLimitInfo.remaining : null;

  //checks if item is unavailable at the time in order to render serviceTimes
  //badge. The badge reads "Available from <next start>" (next start is always
  //computed from now), so it is a statement about the present moment: feeding
  //the chosen pre-order time into it hides the hint precisely for customers
  //planning ahead. The requested time is validated at checkout instead.
  useEffect(() => {
    const checkConditions = () => {
      if (
        activeItemData &&
        activeItemData?.orderConditions?.length > 0 &&
        !hasSatisfiedOrderCondition(activeItemData?.orderConditions) &&
        getServiceTimesByPriorty(activeItemData?.orderConditions)?.length
      ) {
        setRenderServiceTimeBadge(true);
      } else {
        setRenderServiceTimeBadge(false);
      }
    };

    checkConditions();

    const intervalId = setInterval(checkConditions, 60 * 1000);

    return () => clearInterval(intervalId);
  }, [activeItemData]);

  // listens for newly added itemData and sets activeIndex
  useEffect(() => {
    if (item && hasAddedItemData) {
      setActiveIndex(itemData.length - 1); // jump to newly created one
      setHasAddedItemData(false);
    }
  }, [activeIndex, hasAddedItemData, item]);

  // handles the onClick for the add to cart button
  const handleAddToCart = (qty, skipAdditionsModal) => {
    if (isSoldOut) return;

    // Check stock limit: count how many of this item are already in cart
    if (remaining != null) {
      const cartQtyForItem = cart
        ?.filter((ci) => ci.itemData?.id === activeItemData?.id)
        ?.reduce((sum, ci) => sum + (ci.qty || 0), 0) || 0;
      if (cartQtyForItem + qty > remaining) return;
    }

    // need to decide whether to open the select-additions modal or not.
    const itemAdditionGroups = getAvailableItemAdditionGroups(
      activeItemData?.additionGroups,
      checkout?.orderType,
      getOrderConfig(store, checkout?.orderType)?.posIntegration,
      getEffectiveRequestedAt(checkout),
    );

    (!me
      ? createGuest(
          { locale: intl?.locale.split('-')[0] },
          socketContext?.setToken,
        ).then(() => fetchOnAuthenticated())
      : Promise.resolve()
    ).then(() => {
      TagManager.dataLayer({
        dataLayer: {
          event: 'AddToCart',
          customerProfileId: me?.requester?.customerProfile?.id,
          itemDataId: activeItemData?.id,
          itemName,
          itemPrice: intl.formatNumber(
            getItemDataOrAdditionPrice(priceOrderType, activeItemData),
            { format: 'priceCurrency' },
          ),
          itemId: item?.id,
        },
      });

      if (itemAdditionGroups?.length && !skipAdditionsModal) {
        setSelectAdditionsModalData({
          itemData: activeItemData,
          itemAdditionGroups,
        });
        setIsSelectAdditionsModalOpen(true);
      } else {
        addCartItem(cart, activeItemData, [], qty, priceOrderType); // add without additions
      }
    });
  };

  const _handleLike = () => {
    TagManager.dataLayer({
      dataLayer: {
        event: 'LikeItemData',
        customerProfileId: me?.requester?.customerProfile?.id,
        itemDataId: activeItemData?.id,
        itemName,
        itemPrice: intl.formatNumber(
          getItemDataOrAdditionPrice(priceOrderType, activeItemData),
          { format: 'priceCurrency' },
        ),
        itemId: item?.id,
      },
    });

    return handleLike(
      me,
      intl,
      item.id,
      isAdminMode,
      isLiked,
      setShowLikeAnimation,
      fetchOnAuthenticated,
      () => {
        fetchMe();
        fetchMenuEntries(store.menu.id, isAdminMode, undefined, store.id);
      },
      socketContext.setToken,
    );
  };

  const openItemInfoModal = () => {
    setItemInfoModalData({
      itemData: {
        ...activeItemData,
        itemId: item?.id,
      },
      categories: item?.categories,
    });
    setIsItemInfoModalOpen(true);
  };

  const openCreateVariationModal = () => {
    setCreateItemModalData({
      isVariation: true,
      itemData: { ...activeItemData, priceInhouse: 0, priceTakeaway: 0 },
      targetItemId: item?.id,
    });
    setIsCreateItemModalOpen(true);
  };

  const openEditModal = () => {
    setEditItemModalData({
      isVariation: true,
      itemData: activeItemData,
      targetItemId: item?.id,
    });

    setIsEditItemModalOpen(true);
  };

  const openEditCategoryModal = () => {
    setEditMenuEntryCategoryModalData({ item: item });
    setIsEditMenuEntryCategoryModalOpen(true);
  };

  const itemImageClassNames = classnames('h-100 mh-10 img-cover-center', {
    'card-img': !isAdminMode && (displayImageOnly || !itemNameVisible),
    'card-img-top':
      (!displayImageOnly && itemNameVisible) ||
      (isAdminMode && (displayImageOnly || !itemNameVisible)),
  });
  const itemNameClassNames = classnames('px-2 py-1');
  const itemNameSpanClassNames = classnames('font-medium-2 m-0 d-inline', {
    'font-italic text-muted':
      !itemNameVisible && isAdminMode && !itemImageVisible,
  });
  const itemPreferencesSpanClassNames = classnames('ml-50', {
    opacity4: !itemNameVisible && isAdminMode && !itemImageVisible,
  });

  const extractTextFromHTML = (htmlString) => {
    const tempElement = document.createElement('div');
    tempElement.innerHTML = htmlString;
    return tempElement.textContent || tempElement.innerText || '';
  };

  const extractedText = extractTextFromHTML(itemDescription);

  if (displayType === 'hidden' && !isAdminMode) {
    return null;
  }

  return (
    <>
      <Col xl="4" lg="4" md="4" sm="6" xs="12" className="cursor-pointer">
        <Card data-testid="itemCardOrder">
          {item ? (
            (itemHasVariations || itemImageVisible) && (
              <div className="d-flex flex-grow-1" onClick={openItemInfoModal}>
                {itemHasVariations ? (
                  <ItemCardCarousel
                    activeIndex={activeIndex}
                    setActiveIndex={setActiveIndex}
                    item={item}
                    itemImageClassNames={itemImageClassNames}
                  />
                ) : (
                  <LazyImage
                    image={activeItemData?.image}
                    variant="menuEntry"
                    className={itemImageClassNames}
                    placeholderContainerClassNames="h-100 mh-10 w-100 line-height-1 d-block"
                    placeholderClassNames="mh-10"
                  />
                )}
              </div>
            )
          ) : (
            <SkeletonLoader
              height="100%"
              containerClassName="mh-10 card-img-top"
              style={{ top: '-3px', zIndex: 10 }}
            />
          )}

          {item ? (
            (itemNameVisible || (isAdminMode && !itemImageVisible)) && (
              <CardBody
                onClick={openItemInfoModal}
                className={itemNameClassNames}
              >
                <div className=" align-items-center text-container-marquee">
                  <Marquee>
                    <h1
                      data-testid="itemName"
                      className={itemNameSpanClassNames}
                    >
                      {itemName}
                      {preferenceImages?.length > 0 && preferenceImages}
                    </h1>
                  </Marquee>
                </div>
                {itemNameVisible && itemDescription && (
                  <span className="d-block font-small-3 mt-50">
                    {trimText(extractedText, 80, ' ...')}
                  </span>
                )}
              </CardBody>
            )
          ) : (
            <CardBody className={itemNameClassNames}>
              <div className="d-block">
                <span className={itemNameSpanClassNames}>
                  <SkeletonLoader width="80%" />
                </span>
              </div>
              <span className="d-block font-small-3 mt-50">
                <SkeletonLoader count={2} />
              </span>
            </CardBody>
          )}

          <CardFooter
            className={classnames(
              'bg-white d-flex justify-content-between align-items-center p-0',
              { opacity4: isAdminMode && displayHidden },
            )}
          >
            {item ? (
              <>
                <div
                  className="d-flex align-items-center pl-2 py-1"
                  onClick={_handleLike}
                >
                  <Icon.Heart
                    data-testid="favoritesButton"
                    size={22}
                    fill={isAdminMode || !isLiked ? 'transparent' : '#c51b1b'}
                    strokeWidth={isAdminMode || !isLiked ? '1' : '0'}
                    className={showLikeAnimation ? 'pulse-effect' : ''}
                    onAnimationEnd={() => setShowLikeAnimation(false)}
                  />
                  <span className="font-medium-1 text-bold-500 ml-50">
                    {item?.likesCount}
                  </span>
                </div>
                <div
                  className="d-flex justify-content-between align-items-center pr-2 py-1"
                  style={{
                    minWidth: 0,
                    ...(isSoldOut || (remaining != null && (cart?.filter((ci) => ci.itemData?.id === activeItemData?.id)?.reduce((s, ci) => s + (ci.qty || 0), 0) || 0) >= remaining) ? { opacity: 0.5, pointerEvents: 'none' } : {}),
                  }}
                  onClick={() =>
                    !isAdminMode && _isOrderEnabled && !isSoldOut && handleAddToCart(1)
                  }
                >
                  <span className="font-medium-1 text-bold-500">
                    {activeItemDataPrice === 0 ? (
                      intl.formatMessage({ id: 'priceFree' }).toUpperCase()
                    ) : (
                      <FormattedNumber
                        value={activeItemDataPrice}
                        format="priceCurrency"
                      />
                    )}
                  </span>
                  {!isOrderConditionClassAvailable(
                    checkout?.orderType,
                    activeItemData,
                  ) && (
                    <Badge
                      style={{ zIndex: 9, whiteSpace: 'normal', minWidth: 0 }}
                      color="info"
                      className="ml-1"
                    >
                      <FormattedMessage
                        id={isOrderConditionClassAvailable(
                          checkout?.orderType,
                          activeItemData,
                          true,
                        )}
                      />
                    </Badge>
                  )}
                  {!isAdminMode && posAvailableOrderTypes && (
                    <Badge
                      style={{ zIndex: 9, whiteSpace: 'normal', minWidth: 0 }}
                      color="info"
                      className="ml-1"
                    >
                      <FormattedMessage
                        id="availableForOrderTypesLabel"
                        values={{
                          orderTypes: posAvailableOrderTypes
                            .map((id) => intl.formatMessage({ id }))
                            .join(', '),
                        }}
                      />
                    </Badge>
                  )}
                  {!isAdminMode && _isOrderEnabled && (
                    <Icon.PlusSquare
                      data-testid="addToCartButton"
                      size={22}
                      className={classnames('primary ml-1', {
                        'pulse-effect': showAddToCartAnimation,
                      })}
                      onAnimationEnd={() => setShowAddToCartAnimation(false)}
                    />
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="d-flex align-items-center pl-2 py-1">
                  <Icon.Heart
                    data-testid="favoritesButton2"
                    size={22}
                    fill="transparent"
                    strokeWidth="1"
                  />
                  <span className="font-medium-1 text-bold-500 ml-50">
                    <SkeletonLoader width={60} inline={true} />
                  </span>
                </div>
                <div className="d-flex justify-content-between align-items-center pr-2 py-1">
                  <span className="font-medium-1 text-bold-500">
                    <SkeletonLoader width={80} inline={true} />
                  </span>
                </div>
              </>
            )}
          </CardFooter>

          {item && isAdminMode && (
            <CardFooter className="bg-white p-1 d-flex justify-content-around align-items-center">
              <span onClick={() => setIsRemoveModalOpen(true)}>
                <Icon.Trash2
                  data-testid="removeItemButton"
                  size={22}
                  className="danger"
                />
              </span>
              <span onClick={openEditModal}>
                <Icon.Edit
                  data-testid="editItemButton"
                  id="editBtn"
                  size={22}
                  className="secondary"
                />
              </span>
              <span onClick={openEditCategoryModal}>
                <Icon.Folder
                  data-testid="insertIntoCargeroyButton"
                  size={22}
                  id="editCategory"
                  className="secondary"
                />
              </span>
              <span onClick={openCreateVariationModal}>
                <Icon.Layers
                  data-testid="createItemVariationButton"
                  size={22}
                  className="warning"
                />
              </span>
              {/*  <MenuEntryNameToggle
              id={menuEntry?.id}
              displayText={displayText}
              hasName={!!itemName}
              displayImage={displayImage}
              hasImage={itemHasImages}
              onSuccessCallback={() =>
                fetchMenuEntries(store.menu.id, isAdminMode, undefined, store.id)
              }
              isItemEntry={true}
              itemHasSingleImages={itemHasSingleImages}
            />
            <MenuEntryImageToggle
              id={menuEntry?.id}
              displayText={displayText}
              hasName={!!itemName}
              displayImage={displayImage || itemHasVariations} //itemHasVariations is used as workaround, because items with variations must have images or placeholders. Otherwise you cannot switch between them!
              hasImage={itemHasImages && !itemHasVariations} // same here like above
              onSuccessCallback={() =>
                fetchMenuEntries(store.menu.id, isAdminMode, undefined, store.id)
              }
            /> */}
              <DisplayTypeField
                object={menuEntry}
                onSuccessCallback={() =>
                  fetchMenuEntries(
                    store.menu.id,
                    isAdminMode,
                    undefined,
                    store.id,
                  )
                }
              />
            </CardFooter>
          )}
          <div
            style={{
              position: 'absolute',
              top: '7px',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 10,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              maxWidth: 'calc(100% - 14px)',
            }}
          >
            {isSoldOut && (
              <Badge
                style={{ zIndex: 11, whiteSpace: 'normal', textAlign: 'center' }}
                color="danger"
                className="mb-1"
              >
                <FormattedMessage id="soldOut" />
              </Badge>
            )}
            {!isSoldOut && remaining != null && remaining <= 5 && (
              <Badge
                style={{ zIndex: 11, whiteSpace: 'normal', textAlign: 'center' }}
                color="warning"
                className="mb-1"
              >
                <FormattedMessage
                  id="remainingCount"
                  values={{ count: remaining }}
                />
              </Badge>
            )}
            {renderServiceTimeBadge && !isSoldOut && (
              <>
                <Badge
                  style={{ zIndex: 11, whiteSpace: 'normal', textAlign: 'center' }}
                  color="info"
                  className="mb-1"
                >
                  <FormattedMessage
                    id="limitedTimeOrderNext"
                    values={{
                      nextStartTime: moment(
                        getNextStart(
                          getServiceTimesByPriorty(
                            activeItemData?.orderConditions,
                          ),
                        )?.startAt,
                      ).calendar(),
                    }}
                  />
                </Badge>
              </>
            )}
            {/*  {true &&
              (!remainingMinutes || remainingMinutes === '0') && (
                <>
                  <Badge style={{ zIndex: 11 }} color="info" className="mb-1">
                    <FormattedMessage
                      id="limitedTimeOrderFewMomentsLeft"
                      values={{
                        nextStartTime: moment(nextItemAvaiable).calendar(),
                      }}
                    />
                  </Badge>
                </>
              )} */}
          </div>
        </Card>
      </Col>

      {isAdminMode && (
        <>
          {/*remove item confirmation*/}
          <RemoveConfirmationModal
            isOpen={isRemoveModalOpen}
            closeModal={() => setIsRemoveModalOpen(false)}
            titleId="confirm"
            messageId={
              itemHasVariations
                ? 'removeItemVariationConfirmation'
                : 'removeItemConfirmation'
            }
            handleRemoval={() => {
              // jump back to previous activeIndex, because we are deleting one itemData
              setActiveIndex(
                activeIndex > 0
                  ? activeIndex - 1
                  : Math.max(itemData.length - 2, 0),
              );

              // deletes whole item if there is only one variation
              setTimeout(
                () =>
                  removeItem(
                    itemHasVariations,
                    itemHasVariations ? activeItemData.id : item.id,
                    store?.id,
                    async () => {
                      const menuId = store?.menu?.id;
                      const storeId = store?.id;
                      await Promise.all([
                        fetchFoodItemAdditions(
                          isAdminMode,
                          menuId,
                          undefined,
                          storeId,
                        ),
                        fetchMenuItems(isAdminMode, menuId, undefined, storeId),
                        fetchItemDatas(isAdminMode, menuId, undefined, storeId),
                      ]);
                      await fetchAdditionGroups(
                        isAdminMode,
                        menuId,
                        undefined,
                        storeId,
                      );
                      await fetchMenuEntries(
                        menuId,
                        isAdminMode,
                        undefined,
                        storeId,
                      );
                    },
                  ),
                500,
              );
            }}
          />
        </>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  me: state.me.me,
  store: state.store.store,
  cart: state.store.cart,
  table: state.store.table,
  kiosk: state.store.kiosk,
  menuEntries: state.store.menuEntries,
  checkout: state.store.checkout,
  dailyLimits: state.store.dailyLimits,
});

export default connect(mapStateToProps, {
  setItemInfoModalData,
  setIsItemInfoModalOpen,
  fetchMenuEntries,
  fetchMe,
  addCartItem,
  removeCartItem,
  setSelectAdditionsModalData,
  setIsSelectAdditionsModalOpen,
  setEditItemModalData,
  setIsEditItemModalOpen,
  setCreateItemModalData,
  setIsCreateItemModalOpen,
  fetchOnAuthenticated,
  setEditMenuEntryCategoryModalData,
  setIsEditMenuEntryCategoryModalOpen,
  fetchAdditionGroups,
  fetchFoodItemAdditions,
  fetchMenuItems,
  fetchItemDatas,
})(ItemCard);
